import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import {
  ArrowLeft,
  Smartphone,
  Users,
  User,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  Hash,
  Mail,
  Phone,
  Globe,
  Clock,
  Shield,
  Activity,
  FileText,
  RefreshCw,
  Loader2,
  Building,
  MapPin,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';

const CustomerDeviceAssignmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get } = useApi();

  const [assignment, setAssignment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id) {
      fetchAssignmentDetails({ showFullSkeleton: true });
    }
  }, [id]);

  const fetchAssignmentDetails = async ({ showFullSkeleton = false } = {}) => {
    const shouldShowSkeleton = showFullSkeleton || !assignment;
    try {
      if (shouldShowSkeleton) {
        setIsLoading(true);
        setIsRefreshing(false);
      } else {
        setIsRefreshing(true);
      }
      setError(null);

      const response = await get(`/customer-device-assignments/${id}`);


      // Handle response structure - data might be an array or object
      let assignmentData = null;
      if (response?.success && response?.data) {
        if (Array.isArray(response.data) && response.data.length > 0) {
          assignmentData = response.data[0];
        } else if (typeof response.data === 'object') {
          assignmentData = response.data;
        }
      } else if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        assignmentData = response.data[0];
      } else if (response && typeof response === 'object' && response.id) {
        assignmentData = response;
      }

      if (!assignmentData) {
        throw new Error('Assignment not found');
      }

      setAssignment(assignmentData);
    } catch (error) {
      setError(error.message || 'Failed to fetch assignment details');
      toast.error('Failed to load assignment details');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadgeColor = (isActive) => {
    if (isActive === 1 || isActive === true) {
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100';
    }
    return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100';
  };

  // Show loading state
  if (isLoading && !assignment) {
    return (
      <div className="container mx-auto p-6 max-w-6xl bg-background text-foreground min-h-screen">
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded" />
            <div>
              <Skeleton className="h-6 w-48 mb-2" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <Skeleton className="h-10 w-32" />
        </div>

        {/* Header Card Skeleton */}
        <Card className="border-0 shadow-lg bg-muted/50 mb-8">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <Skeleton className="h-20 w-20 rounded-full" />
              <div className="flex-1 space-y-4">
                <Skeleton className="h-6 w-48" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Combined Details Card Skeleton */}
        <Card className="border-0 shadow-lg bg-muted/50">
          <CardHeader>
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-1/2 space-y-6">
                <Skeleton className="h-6 w-32" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="space-y-3">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-5 w-32" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:w-1/2 space-y-6">
                <Skeleton className="h-6 w-32" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="space-y-3">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-5 w-32" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Show error state
  if (error || !assignment) {
    return (
      <div className="container mx-auto p-6 max-w-6xl bg-background text-foreground min-h-screen">
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Assignment Not Found</h2>
          <p className="text-muted-foreground mb-4">
            {error || 'The requested assignment could not be found.'}
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
            <Button onClick={() => navigate('/admin/customer-device-assignments')}>
              View All Assignments
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const device = assignment?.device || {};
  const deviceType = device?.device_type || {};
  const admin = assignment?.admin || {};
  const account = assignment?.account || {};
  const customers = account?.customers || [];
  const customer = Array.isArray(customers) && customers.length > 0 ? customers[0] : null;
  
  // Get device name
  const deviceName = deviceType?.name || device?.device_name || device?.name || 'N/A';

  return (
    <div className="relative container mx-auto p-6 max-w-6xl bg-background text-foreground min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-2">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="border-0 bg-muted/50"
            size="sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchAssignmentDetails()}
            className="border-0 bg-muted/50"
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </>
            )}
          </Button>
        </div>

        {/* Assignment Header Card */}
        <Card className="border-0 shadow-lg bg-muted/50">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              {/* Assignment Avatar */}
              <Avatar className="h-20 w-20 border-4 border-white dark:border-gray-800 shadow-lg">
                <AvatarImage
                  src={`https://api.dicebear.com/7.x/shapes/svg?seed=${assignment.id}&backgroundColor=3b82f6`}
                />
                <AvatarFallback className="bg-blue-500 text-white text-sm font-bold">
                  <Smartphone className="w-8 h-8" />
                </AvatarFallback>
              </Avatar>

              {/* Assignment Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
                  <h1 className="text-xl font-bold text-foreground">
                    {deviceName}
                  </h1>
                  <Badge
                    className={`${getStatusBadgeColor(assignment.is_active)} border-0 px-2 py-0.5`}
                  >
                    {assignment.is_active === 1 || assignment.is_active === true
                      ? 'ACTIVE'
                      : 'INACTIVE'}
                  </Badge>
                </div>

                <div className="flex flex-wrap items-center gap-4 text-sm">
                  {(device.imei_number || device.imeiNumber) && (
                    <div className="flex items-center gap-2">
                      <Smartphone className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">IMEI:</span>
                      <span className="text-sm font-medium text-base">
                        {device.imei_number || device.imeiNumber}
                      </span>
                    </div>
                  )}
                  {account.name && (
                    <div className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Account:</span>
                      <span className="text-sm font-medium text-base">{account.name}</span>
                    </div>
                  )}
                  {assignment.assigned_at && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Assigned:</span>
                      <span className="text-sm font-medium text-base">
                        {formatDate(assignment.assigned_at).split(',')[0]}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Device Details Card */}
      <Card className="border-0 shadow-lg bg-muted/50">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              <CardTitle className="text-lg">Device Details</CardTitle>
            </div>
            {device.notes && (
              <p className="text-sm text-muted-foreground flex-1 text-right">
               {device.notes}
              </p>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(device.imei_number || device.imeiNumber) && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">IMEI Number</p>
                <p className="font-medium text-base">
                  {device.imei_number || device.imeiNumber}
                </p>
              </div>
            )}
            {device.gps_card_no && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">GPS Card No</p>
                <p className="font-medium text-base">{device.gps_card_no}</p>
              </div>
            )}
            {device.gps_serial_no && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">GPS Serial No</p>
                <p className="font-medium text-base">{device.gps_serial_no}</p>
              </div>
            )}
            {device.sim_card && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">SIM Card</p>
                <p className="font-medium text-base">{device.sim_card}</p>
              </div>
            )}
            {(device.device_status || device.deviceStatus) && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Device Status</p>
                <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100 border-0 px-2 py-0.5">
                  {(device.device_status || device.deviceStatus).toUpperCase()}
                </Badge>
              </div>
            )}
            {device.activate_date && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Activate Date</p>
                <p className="font-medium text-base">{formatDate(device.activate_date)}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {(account.name || account.email || account.phone || account.status) && (
          <Card className="border-0 shadow-md bg-muted/50 hover:shadow-lg transition-shadow">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400">
                    <Building className="w-5 h-5" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle className="text-lg">Account Information</CardTitle>
                  <p className="text-sm text-muted-foreground">Account details</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {account.name && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Account Name</p>
                  <p className="font-medium text-base">{account.name}</p>
                </div>
              )}
              {account.email && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium text-base">{account.email}</p>
                </div>
              )}
              {account.phone && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium text-base">{account.phone}</p>
                </div>
              )}
              {account.status && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 border-0 px-2 py-0.5">
                    {account.status.toUpperCase()}
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {(admin.name || admin.email) && (
          <Card className="border-0 shadow-md bg-muted/50 hover:shadow-lg transition-shadow">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400">
                    <User className="w-5 h-5" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle className="text-lg">Admin Information</CardTitle>
                  <p className="text-sm text-muted-foreground">Admin details</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {admin.name && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Admin Name</p>
                  <p className="font-medium text-base">{admin.name}</p>
                </div>
              )}
              {admin.email && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium text-base">{admin.email}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {customer && (
          <Card className="border-0 shadow-md bg-muted/50 hover:shadow-lg transition-shadow">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400">
                    <Users className="w-5 h-5" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle className="text-lg">Customer Information</CardTitle>
                  <p className="text-sm text-muted-foreground">Customer details</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {customer.name && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Customer Name</p>
                  <p className="font-medium text-base">{customer.name}</p>
                </div>
              )}
              {customer.email && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium text-base">{customer.email}</p>
                </div>
              )}
              {customer.role && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Role</p>
                  <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100 border-0 px-2 py-0.5">
                    {customer.role.toUpperCase()}
                  </Badge>
                </div>
              )}
              {customer.isActive !== undefined && customer.isActive !== null && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge className={`${customer.isActive === 1 || customer.isActive === true ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100' : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100'} border-0 px-2 py-0.5`}>
                    {customer.isActive === 1 || customer.isActive === true ? 'ACTIVE' : 'INACTIVE'}
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {isRefreshing && (
        <div className="absolute inset-0 z-50 flex items-start justify-center pt-24 px-6 bg-background/90 dark:bg-slate-950/80 backdrop-blur-sm transition-opacity">
          <div className="w-full max-w-5xl">
            <div className="rounded-2xl border border-border/40 dark:border-slate-800/60 bg-card/90 dark:bg-slate-900/90 shadow-2xl p-6 space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Skeleton className="h-14 w-14 rounded-xl" />
                <div className="flex-1 w-full space-y-2">
                  <Skeleton className="h-5 w-1/3" />
                  <Skeleton className="h-4 w-1/4" />
                </div>
                <Skeleton className="h-6 w-24 rounded-full" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={`refresh-skeleton-${index}`}
                    className="rounded-xl border border-border/30 dark:border-slate-800/50 bg-muted/60 dark:bg-slate-800/60 p-4 space-y-3"
                  >
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-5 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDeviceAssignmentDetails;

